<template>
  <AdminShell
    :page-title="pageTitle"
    :page-subtitle="pageSubtitle"
    active-menu="overview"
  >
    <div class="live-overview">

    <el-card shadow="never" class="filter-card">
      <div class="toolbar">
        <div class="toolbar-item">
          <span>访问次数最小值</span>
          <el-input
            v-model.trim="filters.minVisits"
            type="number"
            clearable
            placeholder="100"
            @keyup.enter.native="applyFilters"
          />
        </div>
        <div class="toolbar-item">
          <span>访问人数最小值</span>
          <el-input
            v-model.trim="filters.minVisitors"
            type="number"
            clearable
            placeholder="50"
            @keyup.enter.native="applyFilters"
          />
        </div>
        <div class="toolbar-item">
          <span>平均停留时长最小值</span>
          <el-input
            v-model.trim="filters.minAvgDurationSeconds"
            type="number"
            clearable
            placeholder="秒"
            @keyup.enter.native="applyFilters"
          >
            <template slot="append">秒</template>
          </el-input>
        </div>
        <div class="toolbar-item">
          <span>拉流成功率最小值</span>
          <el-input
            v-model.trim="filters.minPlaySuccessRate"
            type="number"
            clearable
            placeholder="0-100"
            @keyup.enter.native="applyFilters"
          >
            <template slot="append">%</template>
          </el-input>
        </div>
        <div class="toolbar-actions">
          <el-button type="primary" @click="applyFilters">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="table-card">
      <el-table
        ref="table"
        v-loading="loading"
        :data="items"
        :default-sort="{ prop: 'visit_count', order: 'descending' }"
        :row-class-name="getRowClassName"
        stripe
        border
        @sort-change="handleSortChange"
        @row-click="handleRowClick"
      >
        <el-table-column
          prop="room_id"
          label="直播间 ID"
          min-width="220"
          sortable="custom"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <el-button type="text" class="room-link" @click.stop="goToVisits(row)">
              {{ row.room_id }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column
          prop="start_time"
          label="开始时间"
          min-width="180"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ formatDateTime(row.start_time) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="visit_count"
          label="访问次数"
          min-width="120"
          sortable="custom"
        />
        <el-table-column
          prop="visitor_count"
          label="访问人数"
          min-width="120"
          sortable="custom"
        />
        <el-table-column
          prop="bounce_rate"
          label="跳失率"
          min-width="120"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ row.bounce_rate_text }}
          </template>
        </el-table-column>
        <el-table-column
          prop="avg_duration_ms"
          label="平均停留时长"
          min-width="150"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ formatDuration(row.avg_duration_ms) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="play_success_rate"
          label="拉流成功率"
          min-width="140"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ row.play_success_rate_text }}
          </template>
        </el-table-column>
        <template slot="empty">
          <el-empty description="暂无直播总览数据" />
        </template>
      </el-table>

      <div class="pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page="page"
          :page-size="limit"
          :total="total"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <el-drawer
      :visible.sync="failureDrawer.visible"
      :size="drawerSize"
      direction="rtl"
      :with-header="false"
    >
      <div class="failure-drawer" v-loading="failureDrawer.loading">
        <div class="failure-drawer__header">
          <div>
            <div class="failure-drawer__title">失败数据大盘</div>
            <div class="failure-drawer__subtitle">
              直播间：{{ failureDrawer.roomId || '--' }}
            </div>
          </div>
          <el-button
            type="text"
            icon="el-icon-close"
            class="failure-drawer__close"
            @click="failureDrawer.visible = false"
          />
        </div>

        <div class="failure-kpis">
          <div class="failure-kpi">
            <div class="failure-kpi__label">总访问数</div>
            <div class="failure-kpi__value">{{ failureDrawer.totalVisits }}</div>
          </div>
          <div class="failure-kpi">
            <div class="failure-kpi__label">失败访问数</div>
            <div class="failure-kpi__value failure-kpi__value--danger">
              {{ failureDrawer.failedVisits }}
            </div>
          </div>
          <div class="failure-kpi">
            <div class="failure-kpi__label">整体失败率</div>
            <div class="failure-kpi__value">{{ failureRateText }}</div>
          </div>
        </div>

        <el-descriptions :column="1" border size="small" class="failure-meta">
          <el-descriptions-item label="首次访问时间">
            {{ formatDateTime(failureDrawer.meta.start_time) }}
          </el-descriptions-item>
          <el-descriptions-item label="最近访问时间">
            {{ formatDateTime(failureDrawer.meta.last_visit_time) }}
          </el-descriptions-item>
        </el-descriptions>

        <div class="failure-toolbar">
          <div class="failure-toolbar__field">
            <span>短停留阈值</span>
            <el-input
              v-model.trim="failureDrawer.shortStayMs"
              type="number"
              placeholder="1200"
              @keyup.enter.native="refreshFailureDrawer"
            >
              <template slot="append">ms</template>
            </el-input>
          </div>
          <div class="failure-toolbar__actions">
            <el-button
              type="primary"
              :disabled="!failureDrawer.roomId"
              @click="refreshFailureDrawer"
            >
              刷新
            </el-button>
          </div>
        </div>

        <el-alert
          title="多标签统计，同一条失败访问可能同时命中多个维度，各项失败占比之和可能超过 100%。"
          type="info"
          :closable="false"
          show-icon
          class="failure-hint"
        />

        <el-alert
          v-if="failureDrawer.error"
          :title="failureDrawer.error"
          type="error"
          :closable="false"
          show-icon
          class="failure-hint"
        />

        <el-table
          :data="failureDrawer.dimensions"
          border
          stripe
          class="failure-table"
        >
          <el-table-column prop="label" label="失败维度" min-width="280" />
          <el-table-column prop="count" label="数量" min-width="90" align="right" />
          <el-table-column label="数据总量占比" min-width="120" align="right">
            <template slot-scope="{ row }">
              {{ row.total_ratio_text || formatPercent(row.total_ratio) }}
            </template>
          </el-table-column>
          <el-table-column label="失败总量占比" min-width="120" align="right">
            <template slot-scope="{ row }">
              {{ row.failed_ratio_text || formatPercent(row.failed_ratio) }}
            </template>
          </el-table-column>
          <template slot="empty">
            <el-empty description="暂无失败维度数据" :image-size="84" />
          </template>
        </el-table>
      </div>
    </el-drawer>
    </div>
  </AdminShell>
</template>

<script>
import AdminShell from '@/components/admin/AdminShell.vue'
import {
  LIVE_STATS_PATHS,
  LEGACY_LIVE_STATS_PATHS,
  fetchLiveStatsJsonWithFallback,
} from '@/utils/live-stats-api'

const DEFAULT_STATS_BASE = process.env.NODE_ENV == 'production' ? 'https://anal.wokfoy.com' : 'https://anal.wokfoy.com'

function createFailureDrawerState() {
  return {
    visible: false,
    loading: false,
    roomId: '',
    shortStayMs: '1200',
    totalVisits: 0,
    failedVisits: 0,
    dimensions: [],
    meta: {
      start_time: 0,
      last_visit_time: 0,
    },
    error: '',
  }
}

export default {
  name: 'LiveOverview',
  components: {
    AdminShell,
  },
  data() {
    return {
      base: DEFAULT_STATS_BASE,
      pageTitle: '\u76f4\u64ad\u6570\u636e\u603b\u89c8',
      pageSubtitle: '\u6309\u76f4\u64ad\u95f4\u805a\u5408\u67e5\u770b\u8bbf\u95ee\u3001\u505c\u7559\u4e0e\u62c9\u6d41\u8868\u73b0\u3002',
      loading: false,
      items: [],
      total: 0,
      limit: 20,
      offset: 0,
      sortBy: 'visit_count',
      sortOrder: 'desc',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1280,
      filters: {
        minVisits: '',
        minVisitors: '',
        minAvgDurationSeconds: '',
        minPlaySuccessRate: '',
      },
      failureDrawer: createFailureDrawerState(),
    }
  },
  computed: {
    page() {
      return Math.floor(this.offset / this.limit) + 1
    },
    drawerSize() {
      return this.viewportWidth <= 768 ? '100%' : '720px'
    },
    failureRateText() {
      const total = Number(this.failureDrawer.totalVisits) || 0
      const failed = Number(this.failureDrawer.failedVisits) || 0
      return this.formatPercent(total > 0 ? failed / total : 0)
    },
  },
  methods: {
    buildQuery() {
      const params = new URLSearchParams()
      params.set('limit', this.limit)
      params.set('offset', this.offset)
      params.set('sort_by', this.sortBy)
      params.set('sort_order', this.sortOrder)

      if (this.filters.minVisits !== '') {
        params.set('min_visits', this.filters.minVisits)
      }
      if (this.filters.minVisitors !== '') {
        params.set('min_visitors', this.filters.minVisitors)
      }
      if (this.filters.minAvgDurationSeconds !== '') {
        params.set('min_avg_duration_ms', Number(this.filters.minAvgDurationSeconds) * 1000)
      }
      if (this.filters.minPlaySuccessRate !== '') {
        params.set('min_play_success_rate', this.filters.minPlaySuccessRate)
      }

      return params.toString()
    },
    normalizeShortStayMs(value) {
      const parsed = Number.parseInt(value, 10)
      if (!Number.isFinite(parsed) || parsed < 0) {
        return 1200
      }
      return Math.min(parsed, 600000)
    },
    async fetchData() {
      this.loading = true
      try {
        const query = this.buildQuery()
        const data = await fetchLiveStatsJsonWithFallback(
          this.base,
          LIVE_STATS_PATHS.liveOverview,
          LEGACY_LIVE_STATS_PATHS.liveOverview,
          query
        )
        this.items = Array.isArray(data.items) ? data.items : []
        this.total = Number(data.total) || 0
      } catch (error) {
        this.items = []
        this.total = 0
        this.$message.error(error?.message || '直播总览加载失败')
      } finally {
        this.loading = false
      }
    },
    async fetchFailureOverview() {
      if (!this.failureDrawer.roomId) return

      const shortStayMs = this.normalizeShortStayMs(this.failureDrawer.shortStayMs)
      this.failureDrawer.shortStayMs = String(shortStayMs)
      this.failureDrawer.loading = true
      this.failureDrawer.error = ''

      try {
        const params = new URLSearchParams()
        params.set('room_id', this.failureDrawer.roomId)
        params.set('short_stay_ms', shortStayMs)

        const data = await fetchLiveStatsJsonWithFallback(
          this.base,
          LIVE_STATS_PATHS.liveFailureOverview,
          LEGACY_LIVE_STATS_PATHS.liveFailureOverview,
          params
        )
        this.failureDrawer.totalVisits = Number(data.total_visits) || 0
        this.failureDrawer.failedVisits = Number(data.failed_visits) || 0
        this.failureDrawer.meta = {
          start_time: Number(data?.meta?.start_time) || 0,
          last_visit_time: Number(data?.meta?.last_visit_time) || 0,
        }
        this.failureDrawer.dimensions = Array.isArray(data.dimensions)
          ? data.dimensions.slice().sort((a, b) => {
            if ((b.count || 0) !== (a.count || 0)) {
              return (b.count || 0) - (a.count || 0)
            }
            return String(a.label || '').localeCompare(String(b.label || ''), 'zh-CN')
          })
          : []
      } catch (error) {
        this.failureDrawer.error = error?.message || '失败数据大盘加载失败'
        this.failureDrawer.totalVisits = 0
        this.failureDrawer.failedVisits = 0
        this.failureDrawer.dimensions = []
        this.failureDrawer.meta = {
          start_time: 0,
          last_visit_time: 0,
        }
      } finally {
        this.failureDrawer.loading = false
      }
    },
    applyFilters() {
      this.offset = 0
      this.fetchData()
    },
    resetFilters() {
      this.filters = {
        minVisits: '',
        minVisitors: '',
        minAvgDurationSeconds: '',
        minPlaySuccessRate: '',
      }
      this.sortBy = 'visit_count'
      this.sortOrder = 'desc'
      this.offset = 0
      this.fetchData()
      this.$nextTick(() => {
        if (this.$refs.table && this.$refs.table.sort) {
          this.$refs.table.sort('visit_count', 'descending')
        }
      })
    },
    handlePageChange(nextPage) {
      this.offset = (nextPage - 1) * this.limit
      this.fetchData()
    },
    handleSortChange({ prop, order }) {
      if (!prop || !order) {
        this.sortBy = 'visit_count'
        this.sortOrder = 'desc'
      } else {
        this.sortBy = prop
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc'
      }
      this.offset = 0
      this.fetchData()
    },
    handleRowClick(row) {
      if (!row || !row.room_id) return
      this.failureDrawer.visible = true
      this.failureDrawer.roomId = row.room_id
      this.failureDrawer.error = ''
      this.fetchFailureOverview()
    },
    refreshFailureDrawer() {
      this.fetchFailureOverview()
    },
    getRowClassName({ row }) {
      if (this.failureDrawer.visible && row && row.room_id === this.failureDrawer.roomId) {
        return 'is-active-row'
      }
      return ''
    },
    handleResize() {
      this.viewportWidth = window.innerWidth
    },
    formatDuration(ms) {
      const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      const pad = value => String(value).padStart(2, '0')
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    },
    formatPercent(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '0.0%'
      return `${(num * 100).toFixed(1)}%`
    },
    formatDateTime(ts) {
      if (!ts) return '--'
      const date = new Date(Number(ts))
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    goToVisits(row) {
      if (!row || !row.room_id) return
      const from = Number(row.start_time)
      const to = Date.now()
      this.$router.push({
        path: '/visitsTable',
        query: {
          room_id: row.room_id,
          ...(Number.isFinite(from) && from > 0 ? { from: String(from) } : {}),
          to: String(to),
        },
      })
    },
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
}
</script>

<style scoped>
.live-overview {
  padding: 0;
  background: #f5f7fb;
  min-height: 100vh;
}

.filter-card,
.table-card {
  border-radius: 16px;
  border: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.toolbar-item {
  width: 220px;
}

.toolbar-item span {
  display: block;
  margin-bottom: 6px;
  color: #344054;
  font-size: 14px;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.room-link {
  padding: 0;
  font-weight: 600;
}

.table-card ::v-deep .el-table__body tr {
  cursor: pointer;
}

.table-card ::v-deep .el-table__body tr.is-active-row > td {
  background: #edf4ff;
}

.failure-drawer {
  height: 100%;
  padding: 24px;
  background: #f8faff;
  overflow-y: auto;
}

.failure-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.failure-drawer__title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a44;
}

.failure-drawer__subtitle {
  margin-top: 6px;
  color: #5b6475;
  word-break: break-all;
}

.failure-drawer__close {
  padding: 0;
  font-size: 20px;
}

.failure-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.failure-kpi {
  padding: 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.failure-kpi__label {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 8px;
}

.failure-kpi__value {
  color: #111827;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.failure-kpi__value--danger {
  color: #dc2626;
}

.failure-meta {
  margin-bottom: 16px;
  background: #fff;
}

.failure-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}

.failure-toolbar__field {
  width: 220px;
}

.failure-toolbar__field span {
  display: block;
  margin-bottom: 6px;
  color: #344054;
  font-size: 14px;
}

.failure-toolbar__actions {
  display: flex;
  gap: 8px;
}

.failure-hint {
  margin-bottom: 16px;
}

.failure-table {
  background: #fff;
}

@media (max-width: 768px) {
  .live-overview {
    padding: 0;
  }

  .toolbar-item,
  .failure-toolbar__field {
    width: 100%;
  }

  .toolbar-actions,
  .failure-toolbar {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .failure-kpis {
    grid-template-columns: 1fr;
  }

  .failure-drawer {
    padding: 16px;
  }
}
</style>
